import { useSessionStore, useUIStore } from '@/stores'
import { authService } from '@/services/authService'
import { formatErrorMessage } from '@utils'
import { emitProcessingMessages, clearProcessingMessages } from '@/utils/processing'
import { useToast } from '@/hooks/useToast'
import Lifeline from '@/components/Lifeline'
import StepInputArea from '@/components/StepInputArea'
import OmpDrawer from './drawers/OmpDrawer'
import OmpStepBanner from './components/OmpStepBanner'
import { isSyncedToUrl, resyncPortalSession } from './utils/portalRecovery'

// Renders the retention lifeline (step + contact form) so it isn't stranded in OMP.
const OmpLifeline = () => {
  const toast = useToast()
  // Steps seen while the recovery walk unwinds aren't the user's context — reacting
  // to their lifeline flag flashes this drawer open and shut.
  const resyncing = useSessionStore(s => s.portalResyncing)
  const isLifelineActive = useSessionStore(s => s.isLifelineActive()) && !resyncing
  const storeSession = useSessionStore(s => s.session) as any
  const popupSession = useSessionStore(s => s.popupSession) as any
  const session = popupSession ?? storeSession
  const canExit = !!session?.can_exit_lifeline

  const submitInput = async (input: any, data?: Record<string, any>) => {
    const store = useSessionStore.getState()
    const current = store.session as any
    if (!current?.id) {
      return
    }
    const ui = useUIStore.getState()
    ui.setLoading(true)
    emitProcessingMessages(null, null)
    try {
      const params: Record<string, any> = { input: input.id }
      if (data && Object.keys(data).length) {
        params.data = data
      }
      const result = await authService.input(current.id, params)
      store.setSession(result)
    } catch (err) {
      toast.error(formatErrorMessage(err))
    } finally {
      clearProcessingMessages()
      ui.setLoading(false)
    }
  }

  const close = async () => {
    const store = useSessionStore.getState()
    const id = (store.session as any)?.id
    useUIStore.getState().clearUIMessages()
    store.resetLifeline()
    store.resetIdleTimer()
    if (id) {
      try {
        // `close` returns the restored step. Discarding it forced a home walk, which
        // 400s with "Can't return home from here" and strands the session.
        store.setSession(await authService.close(id))
      } catch {
        /* the resync below is the fallback */
      }
    }
    if (!isSyncedToUrl()) {
      await resyncPortalSession()
    }
  }

  return (
    <>
      <OmpDrawer isOpen={isLifelineActive} onClose={close} title={canExit ? 'Need a hand?' : 'Wait!'} size="lg">
        {isLifelineActive && session && (
          <>
            <OmpStepBanner session={session} />
            <div className="content-confirmation">
              <StepInputArea session={session} onSelectInput={(input, data) => submitInput(input, data)} />
            </div>
          </>
        )}
      </OmpDrawer>
      <Lifeline />
    </>
  )
}

export default OmpLifeline
